import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Download, LogOut, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { AgeBadge } from "@/components/yarn/Badges";
import { CatFace } from "@/components/yarn/Cat";
import { CreateGameDialog } from "@/components/yarn/CreateGameDialog";
import { GameCard } from "@/components/yarn/GameCard";
import { YarnBall } from "@/components/yarn/YarnBall";
import { useAuth } from "@/context/AuthContext";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { api, errorText } from "@/lib/api";

function LobbyHeader({ user, onLogout }) {
  const install = useInstallPrompt();
  return (
    <header className="topbar sticky top-0 z-30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <YarnBall size={34} />
          <span className="font-display text-2xl font-semibold text-[var(--yarn-terracotta)]">Yarn</span>
        </div>
        <div className="flex items-center gap-2">
          {install && (
            <button className="btn-soft h-10 px-3 text-sm inline-flex items-center gap-1" onClick={install} data-testid="install-app-button">
              <Download size={16} /> <span className="hidden sm:inline">Install</span>
            </button>
          )}
          <Link to="/stories" className="btn-soft h-10 px-3 text-sm inline-flex items-center gap-1.5" data-testid="my-stories-link">
            <BookOpen size={16} /> <span className="hidden sm:inline">Stories</span>
          </Link>
          <span className="hidden sm:inline font-display" data-testid="lobby-username">{user.username}</span>
          <Link to="/cats" aria-label="Dress up your cat" className="transition-transform duration-200 hover:scale-110" data-testid="lobby-user-cat-link">
            <CatFace id={user.id} look={user.cat} size={40} testid="lobby-user-cat" />
          </Link>
          <AgeBadge rating={user.is_adult ? "adult" : "underage"} testid="lobby-user-age-badge" />
          <button className="btn-soft h-10 w-10 grid place-items-center" onClick={onLogout} aria-label="Log out" data-testid="logout-button">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </header>
  );
}

function JoinByCode({ onJoin }) {
  const [code, setCode] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (code.length === 6) onJoin(code); }} className="patch p-5 sm:p-6 flex flex-col gap-4">
      <div>
        <h2 className="font-display text-lg font-semibold">Got a game code?</h2>
        <p className="text-sm text-[var(--yarn-muted)]">Private games need the 6-character code from the host.</p>
      </div>
      <div className="flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
          placeholder="ABC123" className="field h-12 px-4 flex-1 min-w-0 font-display tracking-[0.3em] text-lg uppercase"
          aria-label="Game code" data-testid="join-code-input" />
        <button type="submit" className="btn-yarn h-12 px-6" disabled={code.length !== 6} data-testid="join-code-button">Join</button>
      </div>
    </form>
  );
}

function CatCard({ user }) {
  return (
    <Link to="/cats" className="patch p-5 sm:p-6 flex items-center gap-4 transition-transform duration-200 hover:-translate-y-0.5 md:col-span-2 lg:col-span-1" data-testid="cat-closet-card">
      <CatFace id={user.id} look={user.cat} size={84} className="yarn-bob" />
      <div className="min-w-0">
        <h2 className="font-display text-lg font-semibold">Dress up your cat</h2>
        <p className="text-sm text-[var(--yarn-muted)]">Pick fur, eyes, hats and seasonal outfits, and save a whole crew of cats.</p>
      </div>
    </Link>
  );
}

export default function LobbyPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/rooms").then((r) => setGames(r.data)).catch(() => setGames((g) => g || []));
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const join = async (code) => {
    try {
      await api.get(`/rooms/${code}`);
      navigate(`/room/${code}`);
    } catch (err) {
      toast.error(errorText(err));
      load();
    }
  };

  return (
    <div className="flex-1" data-testid="lobby-page">
      <LobbyHeader user={user} onLogout={signOut} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="rise-in">
          <p className="font-display text-sm uppercase tracking-widest text-[var(--yarn-terracotta)]">The lobby</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold mt-2" data-testid="lobby-heading">
            Pick up a thread, {user.username}.
          </h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          <div className="patch patch-stitched p-5 sm:p-6 flex flex-col justify-between gap-4 bg-[var(--yarn-linen)]">
            <div>
              <h2 className="font-display text-lg font-semibold">Start your own story</h2>
              <p className="text-sm text-[var(--yarn-muted)]">Choose a theme, a timer and who gets in. You run the room.</p>
            </div>
            <button className="btn-yarn h-12 px-6 self-start inline-flex items-center gap-2" onClick={() => setCreateOpen(true)} data-testid="create-game-button">
              <Plus size={18} /> Make a game
            </button>
          </div>
          <JoinByCode onJoin={join} />
          <CatCard user={user} />
        </div>

        <div className="flex items-center justify-between mt-12 mb-4">
          <h2 className="font-display text-lg md:text-lg font-semibold">Open games</h2>
          <button className="btn-soft h-10 px-4 text-sm inline-flex items-center gap-2" onClick={load} data-testid="refresh-games-button">
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
        {games === null ? (
          <div className="py-16 grid place-items-center"><YarnBall size={44} className="yarn-spin" /></div>
        ) : games.length === 0 ? (
          <div className="patch p-10 text-center" data-testid="no-games-message">
            <YarnBall size={48} color="#D97706" className="mx-auto yarn-bob" />
            <p className="font-display text-lg mt-4">No public games right now.</p>
            <p className="text-sm text-[var(--yarn-muted)] mt-1">Start one and invite your friends.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="games-list">
            {games.map((g, i) => <GameCard key={g.code} game={g} index={i} onJoin={join} />)}
          </div>
        )}
      </main>
      {createOpen && <CreateGameDialog open={createOpen} onOpenChange={setCreateOpen} user={user} />}
    </div>
  );
}
